import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import throttle from 'lodash/throttle';
import {
  ENCRYPTED_EVENT_TYPE,
  MESSAGE_TYPE,
  getRoomMessageBody,
  getLastMessagePreview,
  isRoomMessageEvent,
  isReplacementEvent,
  getReplacementTargetEventId,
  getEffectiveMessageBodyForEvent,
  buildReplacementBodyByEventId,
} from './matrixRoomUtils';
import { markRoomAsRead } from './matrixReadUtils';
import Matrix from '../../components/matrix/index.js';
import { captureChatException, captureChatMessage } from 'shared/sentry';
import { isAbortError } from '../isAbortError.js';
import {
  CHAT_MESSAGE_PAGE_SIZE,
  CHAT_MESSAGE_BUFFER_MAX,
  canPaginateBackwards,
  countDisplayableTimelineMessages,
  findMessageIndexByKey,
  getLiveTimelineEventCount,
  getMessageStableKey,
  getOldestKeyForLatestPage,
  getVisibleMessageWindow,
  hasOlderLocalMessages,
  mergeRetainedUiMessages,
  paginateUntilNewUiMessages,
  paginateUntilUiMessageCount,
  shiftVisibleWindowOlder,
  shouldPrefetchOlderMessages,
  trimMessagesAroundVisible,
} from './matrixChatPagination';

// Keep a bounded in-memory window so the UI can paginate older messages
// without unbounded growth.
const MAX_ROOM_MESSAGES = CHAT_MESSAGE_BUFFER_MAX;

const compareUiMessagesChronologically = (a, b) => {
  const aTs = Number(a?.ts) || 0;
  const bTs = Number(b?.ts) || 0;
  if (aTs !== bTs) return aTs - bTs;
  return getMessageStableKey(a).localeCompare(getMessageStableKey(b));
};

async function primeRoomToMessagePage(client, room, pageSize) {
  if (!client || !room) {
    return { count: 0, hasMore: false, exhausted: false };
  }
  const target = pageSize > 0 ? pageSize : CHAT_MESSAGE_PAGE_SIZE;
  const existing = countDisplayableTimelineMessages(room);
  if (existing >= target) {
    // A missing prev_batch after /sync is ambiguous (token may land on a
    // later sync). Never treat "no token yet" as confirmed start-of-room —
    // that incorrectly shows "Beginning of conversation" while history exists.
    const hasToken = canPaginateBackwards(room);
    return {
      count: existing,
      hasMore: true,
      exhausted: false,
      // Hint for callers: token present ⇒ server can page; absent ⇒ wait for sync.
      awaitingPaginationToken: !hasToken,
    };
  }

  return paginateUntilUiMessageCount({
    client,
    room,
    getUiMessageCount: countDisplayableTimelineMessages,
    targetCount: target,
  });
}

function formatChatTime(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}

/**
 * Manage messages for a Matrix room:
 * - loads from local room timeline
 * - keeps messages updated via sync/timeline/decrypted listeners
 * - sends text messages
 */
export const useMatrixRoomMessages = ({
  client,
  isInitialized,
  roomId,
  myMatrixUserId,
  otherDisplayName,
  options = {},
}) => {
  const {
    fallbackTimeoutMs = 6000,
    pageSize = CHAT_MESSAGE_PAGE_SIZE,
  } = options;
  const messagePageSize =
    typeof pageSize === 'number' && pageSize > 0
      ? Math.floor(pageSize)
      : CHAT_MESSAGE_PAGE_SIZE;

  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sendLoading, setSendLoading] = useState(false);
  const [paginateLoading, setPaginateLoading] = useState(false);
  const [serverHasMore, setServerHasMore] = useState(true);
  const [historyExhausted, setHistoryExhausted] = useState(false);
  const [oldestVisibleKey, setOldestVisibleKey] = useState(null);
  const [error, setError] = useState(null);

  const isReady = Boolean(client && isInitialized && roomId);
  const lastMarkedReadEventIdRef = useRef(null);
  const markReadInFlightEventIdRef = useRef(null);
  const paginateInFlightRef = useRef(false);
  const paginatePromiseRef = useRef(null);
  const paginationEpochRef = useRef(0);
  const prefetchSuspendedRef = useRef(false);
  const hasMoreBackwardsRef = useRef(true);
  const serverHasMoreRef = useRef(true);
  const historyExhaustedRef = useRef(false);
  const oldestVisibleKeyRef = useRef(null);
  const timelineRecoveryInFlightRef = useRef(false);
  /** Lazy back-pagination once per open when /sync left a thin timeline */
  const timelinePrimedForRoomRef = useRef(null);
  const primeInFlightRef = useRef(null);
  const runPrimeTimelineRef = useRef(() => {});
  /** Avoid clearing the list on client/init flaps; only reset when the room changes */
  const messagesRoomIdRef = useRef(undefined);

  // Refs so timeline/sync/decrypted listeners always use latest callbacks and roomId
  // without causing the effect to re-run (which would remove/re-add listeners and risk missing events).
  const roomIdRef = useRef(roomId);
  const loadFromRoomRef = useRef(null);
  const mapMatrixEventToUiMessageRef = useRef(null);
  const buildUiMessagesFromRoomRef = useRef(null);
  const markCurrentRoomAsReadRef = useRef(null);
  const upsertUiMessageRef = useRef(null);
  const messagesRef = useRef([]);
  const sendInFlightRef = useRef(false);

  useEffect(() => {
    // Reset pagination state on room change.
    paginationEpochRef.current += 1;
    paginateInFlightRef.current = false;
    paginatePromiseRef.current = null;
    prefetchSuspendedRef.current = false;
    hasMoreBackwardsRef.current = true;
    serverHasMoreRef.current = true;
    historyExhaustedRef.current = false;
    oldestVisibleKeyRef.current = null;
    timelinePrimedForRoomRef.current = null;
    primeInFlightRef.current = null;
    setServerHasMore(true);
    setHistoryExhausted(false);
    setOldestVisibleKey(null);
    setPaginateLoading(false);
    sendInFlightRef.current = false;
  }, [roomId]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    oldestVisibleKeyRef.current = oldestVisibleKey;
  }, [oldestVisibleKey]);

  const visibleMessages = useMemo(
    () => getVisibleMessageWindow(messages, oldestVisibleKey, messagePageSize),
    [messages, oldestVisibleKey, messagePageSize]
  );

  // hasMore until the homeserver confirms EOF (historyExhausted). Local older
  // rows always count as "more" even after EOF was wrongly set.
  const hasMoreBackwards = useMemo(
    () =>
      hasOlderLocalMessages(messages, oldestVisibleKey, messagePageSize) ||
      !historyExhausted,
    [messages, oldestVisibleKey, messagePageSize, historyExhausted]
  );

  const isAtConversationStart = useMemo(
    () =>
      historyExhausted &&
      !hasOlderLocalMessages(messages, oldestVisibleKey, messagePageSize) &&
      !paginateLoading,
    [historyExhausted, messages, oldestVisibleKey, messagePageSize, paginateLoading]
  );

  useEffect(() => {
    hasMoreBackwardsRef.current = hasMoreBackwards;
  }, [hasMoreBackwards]);

  const markCurrentRoomAsReadIfNeeded = useCallback(
    async (room) => {
      if (!client || !room) return;
      const rid = roomIdRef.current;
      if (!rid || rid !== room.roomId) return;

      const preferredEvent = getLastMessagePreview(room)?.event || room.getLastLiveEvent?.();
      const eventId = preferredEvent?.getId?.();
      if (!eventId) return;

      if (lastMarkedReadEventIdRef.current === eventId) return;
      // Avoid duplicate network calls while a previous mark is in-flight.
      if (markReadInFlightEventIdRef.current === eventId) return;
      markReadInFlightEventIdRef.current = eventId;

      const res = await markRoomAsRead(client, room, { event: preferredEvent });
      if (res?.success) {
        lastMarkedReadEventIdRef.current = eventId;
      } else if (res?.error) {
        captureChatMessage('Matrix: failed to mark room as read', 'warning', {
          extra: { error: res.error },
          tags: { feature: 'matrix-chat', action: 'mark-read' },
        });
      }

      if (markReadInFlightEventIdRef.current === eventId) {
        markReadInFlightEventIdRef.current = null;
      }
    },
    [client]
  );

  const markCurrentRoomAsReadThrottled = useMemo(
    () =>
      throttle(markCurrentRoomAsReadIfNeeded, 2000, { leading: true, trailing: true }),
    [markCurrentRoomAsReadIfNeeded]
  );
  useEffect(() => {
    return () => markCurrentRoomAsReadThrottled.cancel?.();
  }, [markCurrentRoomAsReadThrottled]);

  const mapMatrixEventToUiMessage = useCallback(
    (event, timelineEvents = [], effectiveBody = null) => {
      if (!event) return null;
      // Encrypted on the wire but not yet usable as a message — show a placeholder until decryption.
      if (event.getType?.() === ENCRYPTED_EVENT_TYPE && !isRoomMessageEvent(event)) {
        const senderId = event.getSender?.() || '';
        const isMine = Boolean(myMatrixUserId && senderId === myMatrixUserId);
        const ts = event.getTs?.() || Date.now();
        const eventId = event.getId?.() || null;
        const id =
          eventId ||
          (typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${ts}-${senderId}-${Math.random().toString(16).slice(2)}`);
        return {
          id,
          eventId,
          txnId: null,
          isMine,
          senderId,
          senderName: isMine ? 'You' : otherDisplayName || 'Unknown',
          message: 'Decrypting...',
          ts,
          time: formatChatTime(ts),
          pending: true,
        };
      }
      if (!isRoomMessageEvent(event)) return null;
      // Edit (m.replace) events are not standalone messages; they update the original event.
      if (isReplacementEvent(event)) return null;

      const body =
        typeof effectiveBody === 'string'
          ? effectiveBody
          : timelineEvents.length > 0
            ? getEffectiveMessageBodyForEvent(event, timelineEvents)
            : getRoomMessageBody(event);
      if (!body) return null;

      const senderId = event.getSender?.() || '';
      const isMine = Boolean(myMatrixUserId && senderId === myMatrixUserId);
      const ts = event.getTs?.() || Date.now();
      const eventId = event.getId?.();
      // For local echoes / pending events, txnId is often the only stable identifier.
      const txnId = event.getTxnId?.() || event.getUnsigned?.()?.transaction_id || null;

      return {
        id: eventId || txnId || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${ts}-${senderId}-${Math.random().toString(16).slice(2)}`),
        eventId: eventId || null,
        txnId: txnId || null,
        isMine,
        senderId,
        senderName: isMine ? 'You' : otherDisplayName || 'Unknown',
        message: body,
        ts,
        time: formatChatTime(ts),
      };
    },
    [myMatrixUserId, otherDisplayName]
  );

  const buildUiMessagesFromRoom = useCallback(
    (room, options = {}) => {
      if (!room) return [];

      const { sort = true } = options;
      const rawEvents = room.getLiveTimeline?.().getEvents?.() || room.timeline || [];
      const windowedEvents = Array.isArray(rawEvents)
        ? rawEvents.slice(-MAX_ROOM_MESSAGES)
        : [];

      let events = windowedEvents.filter(
        (event) => isRoomMessageEvent(event) || event.getType?.() === ENCRYPTED_EVENT_TYPE
      );
      // Live timeline is already chronological; only sort when using legacy room.timeline or explicit sort.
      if (sort) events = [...events].sort((a, b) => (a.getTs?.() || 0) - (b.getTs?.() || 0));
      const replacementBodies = buildReplacementBodyByEventId(events);
      return events
        .filter((event) => !isReplacementEvent(event))
        .map((event) => {
          const eventId = event.getId?.();
          const editedBody = eventId ? replacementBodies.get(eventId) : undefined;
          return mapMatrixEventToUiMessage(event, [], editedBody);
        })
        .filter(Boolean);
    },
    [mapMatrixEventToUiMessage]
  );

  // Merge message into chronological list without full sort when order is already correct.
  const mergeChronological = useCallback((list, uiMessage, replaceIdx = -1) => {
    const trim = (arr) =>
      arr.length > MAX_ROOM_MESSAGES ? arr.slice(-MAX_ROOM_MESSAGES) : arr;
    // Binary search: insertion index that keeps chronological order (O(log n)).
    // Uses upper-bound insertion for equal timestamps so already-rendered items keep position.
    const indexForMessage = (arr, item) => {
      let lo = 0;
      let hi = arr.length;
      while (lo < hi) {
        const mid = (lo + hi) >>> 1;
        if (compareUiMessagesChronologically(arr[mid], item) <= 0) lo = mid + 1;
        else hi = mid;
      }
      return lo;
    };
    if (replaceIdx >= 0) {
      const next = [...list];
      next[replaceIdx] = { ...uiMessage, pending: false };
      const prevItem = next[replaceIdx - 1] ?? null;
      const nextItem = next[replaceIdx + 1] ?? null;
      const inOrderWithPrev =
        prevItem == null || compareUiMessagesChronologically(prevItem, uiMessage) <= 0;
      const inOrderWithNext =
        nextItem == null || compareUiMessagesChronologically(uiMessage, nextItem) <= 0;
      if (inOrderWithPrev && inOrderWithNext) return trim(next);
      const sorted = [...next].sort(compareUiMessagesChronologically);
      return trim(sorted);
    }
    // Append path: new message is usually newest.
    if (list.length === 0) return trim([uiMessage]);
    const lastItem = list[list.length - 1];
    if (compareUiMessagesChronologically(lastItem, uiMessage) <= 0) {
      return trim([...list, uiMessage]);
    }
    // Out-of-order insert (e.g. backfill): binary search then splice, O(log n) + O(n) vs full sort.
    const idx = indexForMessage(list, uiMessage);
    const next = [...list.slice(0, idx), uiMessage, ...list.slice(idx)];
    return trim(next);
  }, []);

  const upsertUiMessage = useCallback((uiMessage) => {
    if (!uiMessage) return;

    setMessages((prev) => {
      const txnId = uiMessage.txnId;

      if (txnId) {
        const replaceIdx = prev.findIndex(
          (m) => m.pending && (m.id === txnId || m.txnId === txnId)
        );
        if (replaceIdx >= 0) return mergeChronological(prev, uiMessage, replaceIdx);
      }

      if (uiMessage.eventId) {
        const existingIdx = prev.findIndex((m) => m.eventId === uiMessage.eventId);
        if (existingIdx >= 0) {
          const existing = prev[existingIdx];
          if (existing.pending || existing.message !== uiMessage.message) {
            return mergeChronological(prev, uiMessage, existingIdx);
          }
          return prev;
        }
      }

      return mergeChronological(prev, uiMessage, -1);
    });
  }, [mergeChronological]);

  const adoptVisibleWindowKey = useCallback(
    (nextMessages, previousKey = oldestVisibleKeyRef.current) => {
      if (!nextMessages.length) {
        oldestVisibleKeyRef.current = null;
        setOldestVisibleKey(null);
        return null;
      }
      const nextKey =
        previousKey && findMessageIndexByKey(nextMessages, previousKey) >= 0
          ? previousKey
          : getOldestKeyForLatestPage(nextMessages, messagePageSize);
      if (nextKey === oldestVisibleKeyRef.current) return nextKey;
      oldestVisibleKeyRef.current = nextKey;
      setOldestVisibleKey(nextKey);
      return nextKey;
    },
    [messagePageSize]
  );

  const loadFromRoom = useCallback(() => {
    if (!isReady) return false;
    const room = client.getRoom?.(roomId);
    if (!room) return false;

    // A live prev_batch means the homeserver still has (or may have) older
    // events. Clear any prior false exhaustion and restore pagination.
    if (canPaginateBackwards(room)) {
      if (historyExhaustedRef.current) {
        historyExhaustedRef.current = false;
        setHistoryExhausted(false);
      }
      serverHasMoreRef.current = true;
      setServerHasMore(true);
    }

    if (paginateInFlightRef.current) return true;
    const built = buildUiMessagesFromRoom(room, { sort: false });
    const newMessages = mergeRetainedUiMessages(messagesRef.current, built);
    const prev = messagesRef.current;
    const lastPrev = prev.length ? prev[prev.length - 1] : null;
    const lastNew = newMessages.length ? newMessages[newMessages.length - 1] : null;
    const firstPrev = prev.length ? prev[0] : null;
    const firstNew = newMessages.length ? newMessages[0] : null;
    const changed =
      newMessages.length !== prev.length ||
      lastNew?.eventId !== lastPrev?.eventId ||
      lastNew?.message !== lastPrev?.message ||
      getMessageStableKey(firstNew) !== getMessageStableKey(firstPrev);
    if (changed) {
      setMessages(newMessages);
      messagesRef.current = newMessages;
    }
    if (newMessages.length && (changed || !oldestVisibleKeyRef.current)) {
      adoptVisibleWindowKey(
        newMessages,
        oldestVisibleKeyRef.current
      );
    }
    markCurrentRoomAsReadRef.current?.(room);
    return true;
  }, [isReady, client, roomId, buildUiMessagesFromRoom, adoptVisibleWindowKey]);

  // Keep refs updated so listeners (registered once per room/client) always see latest callbacks.
  useEffect(() => {
    roomIdRef.current = roomId;
    loadFromRoomRef.current = loadFromRoom;
    mapMatrixEventToUiMessageRef.current = mapMatrixEventToUiMessage;
    buildUiMessagesFromRoomRef.current = buildUiMessagesFromRoom;
    markCurrentRoomAsReadRef.current = markCurrentRoomAsReadThrottled;
    upsertUiMessageRef.current = upsertUiMessage;
  }, [
    roomId,
    loadFromRoom,
    mapMatrixEventToUiMessage,
    buildUiMessagesFromRoom,
    markCurrentRoomAsReadThrottled,
    upsertUiMessage,
  ]);

  const applyPaginationResult = useCallback((result, room, { userFacing = false } = {}) => {
    if (!result) return;
    if (result.error) {
      // Transient errors must never look like start-of-conversation.
      if (userFacing) {
        historyExhaustedRef.current = false;
        setHistoryExhausted(false);
      }
      serverHasMoreRef.current = true;
      setServerHasMore(true);
      return;
    }

    // Background/prime must never flip "Beginning of conversation". Only a
    // user-facing older-history load may confirm start-of-room.
    const exhausted = Boolean(result.exhausted);
    if (userFacing) {
      historyExhaustedRef.current = exhausted;
      setHistoryExhausted(exhausted);
    }

    if (exhausted) {
      serverHasMoreRef.current = false;
      setServerHasMore(false);
      return;
    }

    const more =
      Boolean(result.hasMore) ||
      Boolean(result.awaitingPaginationToken) ||
      (room ? canPaginateBackwards(room) : true);
    serverHasMoreRef.current = more;
    setServerHasMore(more);
  }, []);

  /**
   * Backfill older displayable messages into the in-memory buffer.
   * Does not move the visible window — callers reveal locally after this returns.
   */
  const runServerBackfill = useCallback(
    async (targetNewCount, epoch, { userFacing = false } = {}) => {
      const room = client.getRoom?.(roomId);
      if (!room) return { success: false, loaded: 0, error: 'Room not found' };
      if (!client?.paginateEventTimeline && !client?.scrollback) {
        return { success: false, loaded: 0, error: 'Pagination not supported' };
      }

      const beforeEventCount = getLiveTimelineEventCount(room);
      const result = await paginateUntilNewUiMessages({
        client,
        room,
        getUiMessageCount: countDisplayableTimelineMessages,
        targetNewCount: Math.max(1, targetNewCount),
      });

      if (paginationEpochRef.current !== epoch || roomIdRef.current !== roomId) {
        return { success: false, loaded: 0, cancelled: true };
      }

      const afterEventCount = getLiveTimelineEventCount(room);
      const addedEventCount = Math.max(0, afterEventCount - beforeEventCount);
      const built = buildUiMessagesFromRoom(room, { sort: false });
      const mergedList = mergeRetainedUiMessages(messagesRef.current, built);
      const previousKeys = new Set();
      for (let i = 0; i < messagesRef.current.length; i += 1) {
        const key = getMessageStableKey(messagesRef.current[i]);
        if (key) previousKeys.add(key);
      }
      let loaded = 0;
      for (let i = 0; i < mergedList.length; i += 1) {
        const key = getMessageStableKey(mergedList[i]);
        if (key && !previousKeys.has(key)) loaded += 1;
      }
      if (loaded === 0 && addedEventCount > 0) {
        loaded = Math.max(0, mergedList.length - messagesRef.current.length);
      }

      if (paginationEpochRef.current !== epoch || roomIdRef.current !== roomId) {
        return { success: false, loaded: 0, cancelled: true };
      }

      if (loaded > 0 || mergedList.length !== messagesRef.current.length) {
        const next = trimMessagesAroundVisible(
          mergedList,
          oldestVisibleKeyRef.current,
          messagePageSize,
          MAX_ROOM_MESSAGES
        );
        messagesRef.current = next;
        setMessages((prev) => {
          if (paginationEpochRef.current !== epoch) return prev;
          const rebuilt = mergeRetainedUiMessages(prev, built);
          return trimMessagesAroundVisible(
            rebuilt,
            oldestVisibleKeyRef.current,
            messagePageSize,
            MAX_ROOM_MESSAGES
          );
        });
      }

      if (paginationEpochRef.current !== epoch || roomIdRef.current !== roomId) {
        return { success: false, loaded: 0, cancelled: true };
      }

      if (result.error) {
        prefetchSuspendedRef.current = loaded === 0;
        applyPaginationResult(result, room, { userFacing });
        return { success: false, loaded, error: 'Failed to load older messages' };
      }

      applyPaginationResult(result, room, { userFacing });
      const moreOnServer = serverHasMoreRef.current;
      prefetchSuspendedRef.current = loaded === 0 && moreOnServer;
      return { success: true, loaded };
    },
    [client, roomId, buildUiMessagesFromRoom, messagePageSize, applyPaginationResult]
  );

  const startServerBackfill = useCallback(
    (targetNewCount, options = {}) => {
      if (paginatePromiseRef.current) return paginatePromiseRef.current;
      const epoch = paginationEpochRef.current;
      paginateInFlightRef.current = true;
      const promise = runServerBackfill(targetNewCount, epoch, options).finally(() => {
        if (paginatePromiseRef.current === promise) {
          paginatePromiseRef.current = null;
          paginateInFlightRef.current = false;
        }
      });
      paginatePromiseRef.current = promise;
      return promise;
    },
    [runServerBackfill]
  );

  const revealOlderLocalPage = useCallback(
    (page) => {
      const currentMessages = messagesRef.current;
      const currentOldestKey = oldestVisibleKeyRef.current;
      if (!hasOlderLocalMessages(currentMessages, currentOldestKey, messagePageSize)) {
        return { shifted: false, loaded: 0 };
      }
      const { nextKey, loaded } = shiftVisibleWindowOlder(
        currentMessages,
        currentOldestKey,
        page
      );
      oldestVisibleKeyRef.current = nextKey;
      setOldestVisibleKey(nextKey);
      prefetchSuspendedRef.current = false;
      return { shifted: true, loaded };
    },
    [messagePageSize]
  );

  const prefetchOlderIfNeeded = useCallback(() => {
    if (!isReady) return;
    if (timelineRecoveryInFlightRef.current) return;
    if (prefetchSuspendedRef.current) return;
    if (paginateInFlightRef.current || paginatePromiseRef.current) return;
    if (
      !shouldPrefetchOlderMessages({
        messages: messagesRef.current,
        oldestVisibleKey: oldestVisibleKeyRef.current,
        serverHasMore: serverHasMoreRef.current,
        pageSize: messagePageSize,
        maxCount: MAX_ROOM_MESSAGES,
      })
    ) {
      return;
    }
    void startServerBackfill(messagePageSize, { userFacing: false });
  }, [isReady, messagePageSize, startServerBackfill]);

  /**
   * Reveal the next page of older messages (local window first, then homeserver).
   * Pages by displayable chat rows, not raw timeline events.
   *
   * @param {number} [limit]
   * @returns {Promise<{success:boolean, loaded:number, error?:string, hasMore?:boolean}>}
   */
  const loadOlderMessages = useCallback(
    async (limit) => {
      const page =
        typeof limit === 'number' && limit > 0 ? Math.floor(limit) : messagePageSize;

      if (!isReady) return { success: false, loaded: 0, error: 'Chat is not ready' };

      const room = client.getRoom?.(roomId);
      if (!room) return { success: false, loaded: 0, error: 'Room not found' };

      /**
       * After a successful local reveal: prefetch more if possible, or confirm
       * start-of-conversation when the buffer and homeserver have nothing older.
       */
      const completeLocalReveal = (loaded) => {
        const atLocalStart = !hasOlderLocalMessages(
          messagesRef.current,
          oldestVisibleKeyRef.current,
          messagePageSize
        );
        if (atLocalStart && !canPaginateBackwards(room)) {
          applyPaginationResult(
            { exhausted: true, hasMore: false },
            room,
            { userFacing: true }
          );
        } else {
          prefetchOlderIfNeeded();
        }
        return {
          success: true,
          loaded,
          hasMore: !historyExhaustedRef.current,
        };
      };

      /**
       * Pull the latest displayable rows from the live timeline into React state.
       * Needed when prefetch already loaded history that a prior buffer bound
       * temporarily omitted from the UI list.
       */
      const rehydrateFromLiveTimeline = () => {
        const builtFromRoom = buildUiMessagesFromRoom(room, { sort: false });
        const rehydrated = mergeRetainedUiMessages(messagesRef.current, builtFromRoom);
        const firstChanged =
          getMessageStableKey(rehydrated[0]) !== getMessageStableKey(messagesRef.current[0]);
        if (rehydrated.length === messagesRef.current.length && !firstChanged) {
          return false;
        }
        const bounded = trimMessagesAroundVisible(
          rehydrated,
          oldestVisibleKeyRef.current,
          messagePageSize,
          MAX_ROOM_MESSAGES
        );
        messagesRef.current = bounded;
        setMessages(bounded);
        return true;
      };

      setPaginateLoading(true);
      setError(null);

      try {
        // Let React paint the loader before a synchronous local-window reveal.
        await new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });

        const local = revealOlderLocalPage(page);
        if (local.shifted) {
          return completeLocalReveal(local.loaded);
        }

        rehydrateFromLiveTimeline();
        const afterRehydrate = revealOlderLocalPage(page);
        if (afterRehydrate.shifted) {
          return completeLocalReveal(afterRehydrate.loaded);
        }

        const hasLocalOlder = hasOlderLocalMessages(
          messagesRef.current,
          oldestVisibleKeyRef.current,
          messagePageSize
        );

        // No prev_batch and nothing older in the live timeline ⇒ real start.
        // (Token may return on a later sync; loadFromRoom clears exhaustion then.)
        if (!hasLocalOlder && !canPaginateBackwards(room)) {
          applyPaginationResult(
            { exhausted: true, hasMore: false },
            room,
            { userFacing: true }
          );
          return { success: true, loaded: 0, hasMore: false };
        }

        if (!client?.paginateEventTimeline && !client?.scrollback) {
          return { success: false, loaded: 0, error: 'Pagination not supported' };
        }

        const joinedExisting = Boolean(paginatePromiseRef.current);
        const result = await (
          paginatePromiseRef.current ||
          startServerBackfill(page, { userFacing: true })
        );

        if (roomIdRef.current !== roomId || result?.cancelled) {
          return { success: false, loaded: 0 };
        }

        const afterWait = revealOlderLocalPage(page);
        if (afterWait.shifted) {
          return completeLocalReveal(afterWait.loaded);
        }

        if (joinedExisting && serverHasMoreRef.current && !paginateInFlightRef.current) {
          const retry = await startServerBackfill(page, { userFacing: true });
          if (roomIdRef.current !== roomId || retry?.cancelled) {
            return { success: false, loaded: 0 };
          }
          const afterFetch = revealOlderLocalPage(page);
          if (afterFetch.shifted) {
            return completeLocalReveal(afterFetch.loaded || retry?.loaded || 0);
          }
          const loaded = afterFetch.loaded || retry?.loaded || 0;
          if (retry?.error) {
            return { success: false, loaded, error: retry.error };
          }
          return {
            success: true,
            loaded,
            hasMore: !historyExhaustedRef.current,
          };
        }

        if (result?.error) {
          return { success: false, loaded: result.loaded || 0, error: result.error };
        }

        // Token may still be present but this pass revealed nothing (noise page).
        // Keep hasMore so the next scroll can retry; do not show Beginning yet.
        const stillHasMore =
          !historyExhaustedRef.current &&
          (serverHasMoreRef.current || canPaginateBackwards(room));
        return {
          success: true,
          loaded: 0,
          hasMore: stillHasMore,
        };
      } catch (e) {
        applyPaginationResult({ error: true }, client.getRoom?.(roomId), { userFacing: true });
        const msg = e?.message || 'Failed to load older messages';
        setError(msg);
        return { success: false, loaded: 0, error: msg };
      } finally {
        setPaginateLoading(false);
      }
    },
    [
      isReady,
      client,
      roomId,
      messagePageSize,
      revealOlderLocalPage,
      startServerBackfill,
      prefetchOlderIfNeeded,
      applyPaginationResult,
      buildUiMessagesFromRoom,
    ]
  );

  useEffect(() => {
    if (!isReady || loading) return;
    prefetchOlderIfNeeded();
    // Do not depend on messages.length: a successful prefetch would retrigger
    // this effect and chain /messages requests until the buffer is full.
  }, [isReady, loading, oldestVisibleKey, serverHasMore, prefetchOlderIfNeeded]);

  // Keep messages updated from Matrix events.
  useEffect(() => {
    setError(null);
    lastMarkedReadEventIdRef.current = null;

    if (!roomId) {
      setLoading(false);
      setMessages([]);
      messagesRoomIdRef.current = undefined;
      return;
    }

    const roomChanged = messagesRoomIdRef.current !== roomId;
    messagesRoomIdRef.current = roomId;

    if (!client || !isInitialized) {
      setLoading(false);
      if (roomChanged) setMessages([]);
      return;
    }

    setLoading(true);
    if (roomChanged) {
      setMessages([]);
    }

    let fallbackTimeoutId = null;
    const tryLoad = () => {
      const load = loadFromRoomRef.current;
      if (!load) return false;
      const ok = load();
      if (ok) {
        setLoading(false);
        if (fallbackTimeoutId != null) {
          clearTimeout(fallbackTimeoutId);
          fallbackTimeoutId = null;
        }
      }
      return ok;
    };

    // Do not call tryLoad() here while sync is already usable: loadFromRoom() would
    // immediately setLoading(false) from the thin /sync timeline, before
    // primeTimelineIfSparse finishes backfilling older events — so users saw stale
    // partial history with no "loading" state. Prime always ends with tryLoad()
    // once the Matrix room exists.

    const applyPrimePaginationResult = (result) => {
      const room = client.getRoom?.(roomIdRef.current);
      applyPaginationResult(result, room, { userFacing: false });
      if (result?.error) {
        prefetchSuspendedRef.current = false;
      }
    };

    const primeTimelineIfSparse = async () => {
      const rid = roomIdRef.current;
      const epoch = paginationEpochRef.current;
      if (!rid) {
        tryLoad();
        return;
      }
      if (timelinePrimedForRoomRef.current === rid) {
        tryLoad();
        return;
      }
      if (primeInFlightRef.current) {
        try {
          await primeInFlightRef.current;
        } catch {
          // Prime failure must not block first paint.
        }
        if (roomIdRef.current !== rid || paginationEpochRef.current !== epoch) return;
        tryLoad();
        return;
      }
      if (paginatePromiseRef.current) {
        try {
          await paginatePromiseRef.current;
        } catch {
          // Prefetch failure must not block first paint.
        }
        if (roomIdRef.current !== rid) return;
        if (timelinePrimedForRoomRef.current === rid) {
          tryLoad();
          return;
        }
      }
      const r = client.getRoom?.(rid);
      if (!r) {
        // Room is not in the in-memory store yet (sync still applying). Keep
        // loading; the Room listener will prime once it arrives.
        return;
      }

      const run = (async () => {
        if (typeof Matrix.ensureRoomReady === 'function') {
          try {
            await Matrix.ensureRoomReady(r);
          } catch (e) {
            if (!isAbortError(e)) {
              captureChatException(e, { tags: { feature: 'matrix-chat', action: 'ensure-room-ready' } });
            }
          }
        }
        if (roomIdRef.current !== rid || paginationEpochRef.current !== epoch) return;
        const result = await primeRoomToMessagePage(client, r, messagePageSize);
        if (roomIdRef.current !== rid || paginationEpochRef.current !== epoch) return;
        applyPrimePaginationResult(result);
        timelinePrimedForRoomRef.current = rid;
        tryLoad();
      })();

      primeInFlightRef.current = run;
      try {
        await run;
      } finally {
        if (primeInFlightRef.current === run) {
          primeInFlightRef.current = null;
        }
      }
    };

    const runPrimeTimeline = () => {
      void primeTimelineIfSparse().catch((e) => {
        if (!isAbortError(e)) {
          captureChatException(e, { tags: { feature: 'matrix-chat', action: 'prime-timeline' } });
        }
        tryLoad();
      });
    };

    runPrimeTimelineRef.current = runPrimeTimeline;
    runPrimeTimeline();

    const onSync = (state) => {
      if (timelineRecoveryInFlightRef.current) return;
      if (state !== 'PREPARED' && state !== 'SYNCING' && state !== 'CATCHUP') return;
      if (state === 'PREPARED') {
        timelinePrimedForRoomRef.current = null;
      }
      runPrimeTimeline();
    };

    const DECRYPTED_RELOAD_DELAY_MS = 600; // Delay to allow decrypted event to update room before reloading
    let decryptReloadTimeoutId = null;

    const onTimeline = (event, room, toStartOfTimeline) => {
      if (timelineRecoveryInFlightRef.current) return;
      if (toStartOfTimeline) {
        // Pagination prepends into the live timeline. Skip while priming or while a
        // user-driven page is in flight so we don't flash a thin list or jump the window.
        if (paginateInFlightRef.current) return;
        if (timelinePrimedForRoomRef.current !== roomIdRef.current) return;
        const rid = roomIdRef.current;
        if (room && rid && room.roomId === rid) {
          const load = loadFromRoomRef.current;
          if (load) load();
        }
        return;
      }
      const rid = roomIdRef.current;
      if (!room || room.roomId !== rid) return;

      // Encrypted events are not message events until decrypted; schedule a reload
      // so we pick up the message once Event.decrypted has updated the room.
      if (event?.getType?.() === ENCRYPTED_EVENT_TYPE) {
        const mapEvent = mapMatrixEventToUiMessageRef.current;
        const placeholder = mapEvent?.(event, []);
        if (placeholder) upsertUiMessageRef.current?.(placeholder);
        if (decryptReloadTimeoutId) clearTimeout(decryptReloadTimeoutId);
        decryptReloadTimeoutId = setTimeout(() => {
          decryptReloadTimeoutId = null;
          const load = loadFromRoomRef.current;
          if (load) load();
        }, DECRYPTED_RELOAD_DELAY_MS);
        setLoading(false);
        return;
      }

      if (!isRoomMessageEvent(event)) return;

      // When an edit (m.replace) arrives, upsert the original message with effective body to avoid double UI update from load() + timeline.
      if (isReplacementEvent(event)) {
        const targetId = getReplacementTargetEventId(event);
        const timeline = room.getLiveTimeline?.();
        const timelineEvents = timeline?.getEvents?.() ?? [];
        const messageEvents = timelineEvents.filter(isRoomMessageEvent);
        const originalEvent = targetId
          ? messageEvents.find((ev) => ev.getId?.() === targetId)
          : null;
        const mapEvent = mapMatrixEventToUiMessageRef.current;
        const uiMessage = originalEvent ? mapEvent?.(originalEvent, messageEvents) : null;
        if (uiMessage) upsertUiMessageRef.current?.(uiMessage);
        markCurrentRoomAsReadRef.current?.(room);
        setLoading(false);
        return;
      }

      const timeline = room.getLiveTimeline?.();
      const timelineEvents = timeline?.getEvents?.() ?? [];
      const messageEvents = timelineEvents.filter(isRoomMessageEvent);
      const mapEvent = mapMatrixEventToUiMessageRef.current;
      const uiMessage = mapEvent?.(event, messageEvents);
      if (!uiMessage) {
        setLoading(false);
        return;
      }

      upsertUiMessageRef.current?.(uiMessage);
      markCurrentRoomAsReadRef.current?.(room);
      setLoading(false);
    };

    const onDecrypted = (event, err) => {
      if (timelineRecoveryInFlightRef.current) return;
      if (err) return;
      if (!event) return;
      const decryptedRoomId = event.getRoomId?.() || event.roomId || event?.event?.room_id;
      const rid = roomIdRef.current;
      if (!decryptedRoomId || decryptedRoomId !== rid) return;

      if (!isRoomMessageEvent(event)) {
        setLoading(false);
        return;
      }
      if (isReplacementEvent(event)) {
        const room = client.getRoom?.(decryptedRoomId);
        const targetId = getReplacementTargetEventId(event);
        const timeline = room?.getLiveTimeline?.();
        const timelineEvents = timeline?.getEvents?.() ?? [];
        const messageEvents = timelineEvents.filter(isRoomMessageEvent);
        const originalEvent = targetId
          ? messageEvents.find((ev) => ev.getId?.() === targetId)
          : null;
        const mapEvent = mapMatrixEventToUiMessageRef.current;
        const uiMessage = originalEvent ? mapEvent?.(originalEvent, messageEvents) : null;
        if (uiMessage) upsertUiMessageRef.current?.(uiMessage);
        markCurrentRoomAsReadRef.current?.(room);
        setLoading(false);
        return;
      }
      const room = client.getRoom?.(decryptedRoomId);
      const timeline = room?.getLiveTimeline?.();
      const timelineEvents = timeline?.getEvents?.() ?? [];
      const messageEvents = timelineEvents.filter(isRoomMessageEvent);
      const mapEvent = mapMatrixEventToUiMessageRef.current;
      const uiMessage = mapEvent?.(event, messageEvents);
      if (!uiMessage) {
        setLoading(false);
        return;
      }

      upsertUiMessageRef.current?.(uiMessage);
      markCurrentRoomAsReadRef.current?.(room);
      setLoading(false);
    };

    const safe = (fn, label) => (...args) => {
      try {
        return fn(...args);
      } catch (e) {
        captureChatException(e, {
          tags: { feature: 'matrix-chat', matrix: 'listener' },
          extra: { label },
        });
        return undefined;
      }
    };

    const onTimelineReset = (roomFromEvent) => {
      const rid = roomIdRef.current;
      if (!roomFromEvent || roomFromEvent.roomId !== rid) return;
      if (!client?.scrollback && !client?.paginateEventTimeline) return;
      if (timelineRecoveryInFlightRef.current) return;
      timelineRecoveryInFlightRef.current = true;
      void (async () => {
        try {
          if (paginatePromiseRef.current) {
            await paginatePromiseRef.current;
          }
          const r = client.getRoom?.(rid) || roomFromEvent;
          historyExhaustedRef.current = false;
          setHistoryExhausted(false);
          const result = await primeRoomToMessagePage(client, r, messagePageSize);
          applyPrimePaginationResult(result);
          // Limited /sync starts a new live timeline. A missing token here is
          // not confirmed start-of-room; loadFromRoom restores hasMore when
          // prev_batch lands. Do not force hasMore true — that would call
          // /messages without `from` and restart from the latest events.
          timelinePrimedForRoomRef.current = rid;
          const build = buildUiMessagesFromRoomRef.current;
          if (build) {
            const next = build(r, { sort: false });
            setMessages(next);
            const key = getOldestKeyForLatestPage(next, messagePageSize);
            oldestVisibleKeyRef.current = key;
            setOldestVisibleKey(key);
          }
        } catch (e) {
          if (!isAbortError(e)) {
            captureChatException(e, { tags: { feature: 'matrix-chat', action: 'timeline-reset-recovery' } });
          }
        } finally {
          timelineRecoveryInFlightRef.current = false;
          setLoading(false);
        }
      })();
    };

    const onRoom = (roomFromEvent) => {
      const rid = roomIdRef.current;
      if (!roomFromEvent || roomFromEvent.roomId !== rid) return;
      if (timelinePrimedForRoomRef.current === rid) {
        tryLoad();
        return;
      }
      runPrimeTimeline();
    };

    const onSyncSafe = safe(onSync, 'sync');
    const onTimelineSafe = safe(onTimeline, 'Room.timeline');
    const onTimelineResetSafe = safe(onTimelineReset, 'Room.timelineReset');
    const onDecryptedSafe = safe(onDecrypted, 'Event.decrypted');
    const onRoomSafe = safe(onRoom, 'Room');

    client.on?.('sync', onSyncSafe);
    client.on?.('Room.timeline', onTimelineSafe);
    client.on?.('Room.timelineReset', onTimelineResetSafe);
    client.on?.('Event.decrypted', onDecryptedSafe);
    client.on?.('Room', onRoomSafe);

    let timelineReadyArmed = false;
    const onTimelineReady = (ready) => {
      if (!timelineReadyArmed) {
        timelineReadyArmed = true;
        return;
      }
      if (!ready) return;
      const rid = roomIdRef.current;
      if (!rid) return;
      const room = client.getRoom?.(rid);
      const count = countDisplayableTimelineMessages(room);
      if (count >= messagePageSize) {
        tryLoad();
        return;
      }
      timelinePrimedForRoomRef.current = null;
      prefetchSuspendedRef.current = false;
      runPrimeTimeline();
    };
    const unsubTimelineReady =
      typeof Matrix.subscribeTimelineReady === 'function'
        ? Matrix.subscribeTimelineReady(onTimelineReady)
        : null;

    const thinTimelineRetryId = setTimeout(() => {
      const rid = roomIdRef.current;
      if (!rid) return;
      const room = client.getRoom?.(rid);
      if (!room) {
        runPrimeTimeline();
        return;
      }
      if (countDisplayableTimelineMessages(room) >= messagePageSize) return;
      if (!serverHasMoreRef.current && !canPaginateBackwards(room)) return;
      prefetchSuspendedRef.current = false;
      timelinePrimedForRoomRef.current = null;
      runPrimeTimeline();
    }, 1600);

    fallbackTimeoutId = setTimeout(() => {
      // Keep "Loading messages..." until timeline prime/backfill finishes; the fallback
      // alone must not paint a thin /sync slice as the full thread.
      if (!primeInFlightRef.current) {
        setLoading(false);
      }
      const syncState = client?.getSyncState?.();
      const stillSyncing =
        typeof Matrix.isHealthySyncState === 'function'
          ? Matrix.isHealthySyncState(syncState)
          : syncState === 'SYNCING' || syncState === 'PREPARED' || syncState === 'CATCHUP';
      if (messagesRef.current.length === 0 && !stillSyncing && !primeInFlightRef.current) {
        void Matrix.requestSyncRecovery?.({ reason: 'empty-timeline', force: true });
        setError('Failed to load messages');
      }
    }, fallbackTimeoutMs);

    return () => {
      runPrimeTimelineRef.current = () => {};
      if (fallbackTimeoutId != null) clearTimeout(fallbackTimeoutId);
      clearTimeout(thinTimelineRetryId);
      if (typeof unsubTimelineReady === 'function') unsubTimelineReady();
      if (decryptReloadTimeoutId) {
        clearTimeout(decryptReloadTimeoutId);
        decryptReloadTimeoutId = null;
      }
      client?.removeListener?.('sync', onSyncSafe);
      client?.removeListener?.('Room.timeline', onTimelineSafe);
      client?.removeListener?.('Room.timelineReset', onTimelineResetSafe);
      client?.removeListener?.('Event.decrypted', onDecryptedSafe);
      client?.removeListener?.('Room', onRoomSafe);
    };
  }, [roomId, client, isInitialized, fallbackTimeoutMs, messagePageSize, applyPaginationResult]);

  // Refresh messages when the tab becomes visible so any messages that arrived
  // without firing our timeline/decrypted listeners (e.g. background sync) are shown.
  // iOS Safari also restores from bfcache via pageshow — reload + resume pagination.
  useEffect(() => {
    if (!isReady || !roomId || !client) return;

    const recoverVisibleThread = () => {
      prefetchSuspendedRef.current = false;
      const load = loadFromRoomRef.current;
      if (load) load();

      const room = client.getRoom?.(roomId);
      if (!room) {
        timelinePrimedForRoomRef.current = null;
        runPrimeTimelineRef.current?.();
        return;
      }

      const count = countDisplayableTimelineMessages(room);
      if (count >= messagePageSize) return;
      if (!serverHasMoreRef.current && !canPaginateBackwards(room)) return;

      timelinePrimedForRoomRef.current = null;
      runPrimeTimelineRef.current?.();
    };

    const onVisibilityChange = () => {
      if (document.hidden) return;
      recoverVisibleThread();
    };
    const onPageShow = (event) => {
      if (!event?.persisted) return;
      recoverVisibleThread();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [isReady, roomId, client, messagePageSize]);

  const markAsReadManually = useCallback(() => {
    if (!client || !roomId) return;
    const room = client.getRoom?.(roomId);
    if (room) markCurrentRoomAsReadIfNeeded(room);
  }, [client, roomId, markCurrentRoomAsReadIfNeeded]);

  const sendTextMessage = useCallback(
    async (text) => {
      const trimmed = typeof text === 'string' ? text.trim() : '';
      if (!trimmed) return { success: false, error: 'Message is empty' };

      if (!isReady) {
        return {
          success: false,
          error: 'Chat is not ready yet. Please wait a moment and try again.',
        };
      }

      if (sendInFlightRef.current) {
        return { success: false, skipped: true };
      }
      sendInFlightRef.current = true;

      setSendLoading(true);
      setError(null);

      const now = Date.now();
      const txnId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `m${now}-${Math.random().toString(16).slice(2)}`;
      const myMatrixId = myMatrixUserId || client?.getUserId?.() || '';
      const optimisticMessage = {
        id: txnId,
        eventId: null,
        txnId,
        isMine: true,
        senderId: myMatrixId,
        senderName: 'You',
        message: trimmed,
        ts: now,
        time: formatChatTime(now),
        pending: true,
      };
      setMessages((prev) => {
        const next = [...prev, optimisticMessage];
        if (next.length > MAX_ROOM_MESSAGES) return next.slice(-MAX_ROOM_MESSAGES);
        return next;
      });

      const doSend = () =>
        client.sendEvent?.(
          roomId,
          MESSAGE_TYPE,
          { msgtype: 'm.text', body: trimmed },
          txnId
        );

      try {
        const sendResult = await doSend();
        // Some Matrix SDK versions resolve sendEvent with `{ event_id }`; prefer that when available.
        const eventId =
          (sendResult && (sendResult.event_id || sendResult.eventId)) || null;
        // Real event will replace optimistic via onTimeline/onDecrypted (matched by txnId).
        return { success: true, eventId, txnId };
      } catch (e) {
        // On token expiry: refresh in background and retry once so the send doesn't stop.
        if (Matrix.isTokenExpiredError?.(e)) {
          const refreshed = await Matrix.refreshTokenAndUpdateClient?.();
          if (refreshed) {
            try {
              // Wait 2 seconds after refresh before retrying send
              await new Promise((resolve) => setTimeout(resolve, 2000));
              const retryResult = await doSend();
              const retryEventId =
                (retryResult && (retryResult.event_id || retryResult.eventId)) || null;
              return { success: true, eventId: retryEventId, txnId };
            } catch (retryErr) {
              const retryMsg = retryErr?.message || 'Failed to send message';
              setError(retryMsg);
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === txnId && m.pending ? { ...m, pending: false, failed: true } : m
                )
              );
              return { success: false, error: retryMsg };
            }
          }
          setError('Session expired. Please try again or reload the page.');
          setMessages((prev) =>
            prev.map((m) =>
              m.id === txnId && m.pending ? { ...m, pending: false, failed: true } : m
            )
          );
          return {
            success: false,
            error: 'Session expired. Please try again or reload the page.',
          };
        }

        const msg = e?.message || 'Failed to send message';
        setError(msg);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === txnId && m.pending ? { ...m, pending: false, failed: true } : m
          )
        );
        return { success: false, error: msg };
      } finally {
        sendInFlightRef.current = false;
        setSendLoading(false);
      }
    },
    [client, roomId, isReady, myMatrixUserId]
  );

  /**
   * Send an edit event (m.replace) for an existing message on the stgmatrix server.
   * @param {string} eventId - Matrix event ID of the message to edit
   * @param {string} newBody - New message body (e.g. JSON string with lock_content: false)
   * @returns {Promise<{ success: boolean, error?: string }>}
   */
  const sendEditMessage = useCallback(
    async (eventId, newBody) => {
      if (!eventId || typeof newBody !== 'string') {
        return { success: false, error: 'Event ID and new body are required' };
      }
      if (!isReady || !client || !roomId) {
        return { success: false, error: 'Chat is not ready' };
      }
      try {
        const content = {
          msgtype: 'm.text',
          body: `* ${newBody}`, // fallback text required by Matrix spec
          'm.new_content': { msgtype: 'm.text', body: newBody },
          'm.relates_to': { rel_type: 'm.replace', event_id: eventId },
        };
        await client.sendEvent?.(roomId, MESSAGE_TYPE, content);
        // Optimistic update so UI shows unlocked content (e.g. image/video) without refresh
        setMessages((prev) =>
          prev.map((m) =>
            m.eventId === eventId ? { ...m, message: newBody } : m
          )
        );
        return { success: true };
      } catch (e) {
        const msg = e?.message || 'Failed to edit message';
        return { success: false, error: msg };
      }
    },
    [client, roomId, isReady]
  );

  const api = useMemo(
    () => ({
      loading,
      messages: visibleMessages,
      sendLoading,
      paginateLoading,
      isAtConversationStart,
      historyExhausted,
      hasMoreBackwards,
      error,
      refresh: loadFromRoom,
      loadOlderMessages,
      sendTextMessage,
      sendEditMessage,
      markAsReadManually,
      isReady,
    }),
    [
      loading,
      visibleMessages,
      sendLoading,
      paginateLoading,
      isAtConversationStart,
      historyExhausted,
      hasMoreBackwards,
      error,
      loadFromRoom,
      loadOlderMessages,
      sendTextMessage,
      sendEditMessage,
      markAsReadManually,
      isReady,
    ]
  );

  return api;
};

